import { gql, useQuery } from "@apollo/client";
import PageviewIcon from "@mui/icons-material/Pageview";
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import queryString from "query-string";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBranchContext } from "../../../contexts/branchContext";
import useQueryString from "../../../hooks/useQueryString";
import AdminListContainer from "../../../styles/components/AdminListContainer";
import formatMoney from "../../../utils/formatMoney";
import DataTable from "../../common/DataTable";
import Loading from "../../common/Loading";
import Meta from "../../common/Meta";
import Pagination from "../../common/Pagination";
import Search from "../../common/Search";

export const INVENTORY_SCHEDULES = gql`
  query InventorySchedule {
    __type(name: "InventorySchedule") {
      enumValues {
        name
      }
    }
  }
`;

export const ITEMS = gql`
  query Items(
    $skip: Int
    $take: Int
    $where: ItemWhereInput
    $orderBy: [ItemOrderByWithRelationInput!]
  ) {
    items(skip: $skip, take: $take, where: $where, orderBy: $orderBy) {
      id
      name
      unit
      threshold
      inventoryOrderNumber
      inventorySchedule
      previousQuantity
      lastPurchasedPrice
      itemCategory {
        id
        name
      }
      branch {
        id
        name
      }
    }
  }
`;

const ITEMS_COUNT = gql`
  query ItemsCount($where: ItemWhereInput) {
    itemsCount(where: $where)
  }
`;

const ItemsPage = () => {
  const title = `Items`;
  const { branchFilterQueryString } = useBranchContext();
  const navigate = useNavigate();
  const location = useLocation();
  const paramsObj = queryString.parse(useLocation().search);
  const page = parseInt(paramsObj.page) || 1;
  const perPage = 50;

  const {
    handleChangeQueryString: handleChangeSearch,
    handleSubmitQueryString: handleSubmitSearch,
    queryStringValue: searchTerm,
    initialQueryStringValue: searchQueryString,
  } = useQueryString("search");
  const {
    handleSubmitQueryString: handleSubmitFilter,
    initialQueryStringValue: filterQueryString,
  } = useQueryString("filter");

  const where = {
    ...(filterQueryString && {
      inventorySchedule: {
        equals: filterQueryString,
      },
    }),
    ...(branchFilterQueryString && {
      branch: {
        id: {
          equals: branchFilterQueryString,
        },
      },
    }),
    ...(searchQueryString && {
      OR: [
        {
          name: {
            contains: searchQueryString,
            mode: "insensitive",
          },
        },
      ],
    }),
  };
  const { data: { items } = {}, loading } = useQuery(ITEMS, {
    fetchPolicy: "network-only",
    variables: {
      skip: page * perPage - perPage,
      take: perPage,
      where,
    },
  });

  const {
    data: { __type: { enumValues: inventorySchedules } = {} } = {},
    loading: inventorySchedulesLoading,
  } = useQuery(INVENTORY_SCHEDULES, {
    fetchPolicy: "network-only",
  });

  const handleView = (id) => {
    navigate(`${location.pathname}/${id}`);
  };

  const columns = [
    {
      header: "Actions",
      accessor: "id",
      Cell: (children) =>
        children && (
          <>
            <Tooltip title="View">
              <IconButton
                aria-label="view"
                color="secondary"
                onClick={() => handleView(children)}
                size="large"
              >
                <PageviewIcon />
              </IconButton>
            </Tooltip>
          </>
        ),
    },
    {
      header: "Branch",
      accessor: "branch.name",
    },
    {
      header: "Item Category",
      accessor: "itemCategory.name",
    },
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "Unit",
      accessor: "unit",
    },
    {
      header: "Threshold",
      accessor: "threshold",
      Cell: (children) => children && children.toLocaleString(),
    },
    {
      header: "Inventory Schedule",
      accessor: "inventorySchedule",
      Cell: (children) => children && children.replace(/_/g, " "),
    },
    {
      header: "Inventory Order Number",
      accessor: "inventoryOrderNumber",
    },
    {
      header: "Last Purchased Price",
      accessor: "lastPurchasedPrice",
      Cell: (children) => children && formatMoney(children),
    },
  ];
  if (loading || inventorySchedulesLoading) return <Loading />;
  return (
    <>
      <Meta title={`${title} | Meatsumo`} />
      <AdminListContainer>
        <Typography variant="h3" color="primary" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          alignItems={{ xs: null, md: "flex-end" }}
          spacing={1}
        >
          <FormControl variant="outlined" size="small">
            <InputLabel id="inventorySchedule-label">
              Inventory Schedule
            </InputLabel>
            <Select
              labelId="inventorySchedule-label"
              label="Inventory Schedule"
              id="inventorySchedule"
              name="inventorySchedule"
              value={filterQueryString ?? ""}
              onChange={(e) => {
                handleSubmitFilter("direct")(e);
              }}
              sx={{
                backgroundColor: (theme) => theme.palette.common.white,
                minWidth: "300px",
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {inventorySchedules?.map(({ name }) => (
                <MenuItem key={name} value={name}>
                  {name.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Search
            handleChangeSearch={handleChangeSearch}
            handleSubmitSearch={handleSubmitSearch()}
            searchTerm={searchTerm}
          />
        </Stack>

        <Paper className="dataTableContainer">
          <DataTable data={items} columns={columns} />
        </Paper>
        <Pagination
          query={ITEMS_COUNT}
          variables={{
            where,
          }}
          perPage={perPage}
          page={Number(page)}
        />
      </AdminListContainer>
    </>
  );
};

export default ItemsPage;
